import { authHeaders, getBackendUrl } from "../lib/http";
import { useActiveShopId } from "../store/shop.store";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Package, Loader2, RefreshCw, Eye, Edit3, Trash2,
  ChevronLeft, ChevronRight, Filter, ExternalLink, AlertCircle,
  DollarSign, Tag, Archive, CheckCircle, Clock, XCircle,
} from "lucide-react";
import { clsx } from "clsx";

interface EtsyListing {
  listing_id: number;
  title: string;
  description: string;
  state: string;
  price: { amount: number; divisor: number; currency_code: string };
  quantity: number;
  tags: string[];
  views: number;
  num_favorers: number;
  url: string;
  creation_tsz: number;
  last_modified_tsz: number;
  images?: Array<{
    listing_image_id: number;
    url_170x135: string;
    url_570xN: string;
    url_fullxfull: string;
  }>;
}

interface EtsyListingsResponse {
  count: number;
  results: EtsyListing[];
}

const STATE_OPTIONS = [
  { value: "active", label: "Aktif", icon: CheckCircle, color: "text-emerald-600 bg-emerald-50" },
  { value: "draft", label: "Taslak", icon: Edit3, color: "text-yellow-600 bg-yellow-50" },
  { value: "inactive", label: "Pasif", icon: Clock, color: "text-gray-600 bg-gray-100" },
  { value: "expired", label: "Süresi Dolmuş", icon: XCircle, color: "text-red-600 bg-red-50" },
];

async function fetchListings(state: string, offset: number, limit: number): Promise<EtsyListingsResponse> {
  const params = new URLSearchParams({ state, offset: String(offset), limit: String(limit) });
  const res = await fetch(`${getBackendUrl()}/api/etsy/listings?${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Listeler alınamadı");
  const data = await res.json();
  return data.data;
}

async function deleteListing(listingId: number): Promise<void> {
  const res = await fetch(`${getBackendUrl()}/api/etsy/listings/${listingId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Listing silinemedi");
}

export default function EtsyProductsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [stateFilter, setStateFilter] = useState("active");
  const [offset, setOffset] = useState(0);
  const [selectedListing, setSelectedListing] = useState<EtsyListing | null>(null);
  const limit = 25;
  const shopId = useActiveShopId();

  const { data, isLoading, error, isError } = useQuery({
    queryKey: ["etsy-listings", shopId, stateFilter, offset],
    queryFn: () => fetchListings(stateFilter, offset, limit),
  });

  const deleteM = useMutation({
    mutationFn: deleteListing,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["etsy-listings"] });
    },
  });

  const listings = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  function formatPrice(price: EtsyListing["price"]) {
    return `${(price.amount / price.divisor).toFixed(2)} ${price.currency_code}`;
  }

  function formatDate(timestamp: number) {
    return new Date(timestamp * 1000).toLocaleDateString("tr-TR", {
      day: "numeric", month: "short", year: "numeric",
    });
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="h-6 w-6 text-orange-500" />
            Etsy Ürünlerim
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Mağazanızdaki ürünleri yönetin ({totalCount} ürün)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ["etsy-listings"] })}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            Yenile
          </button>
        </div>
      </div>

      {/* State Filters */}
      <div className="mb-4 flex items-center gap-2">
        <Filter className="h-4 w-4 text-gray-400" />
        {STATE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => { setStateFilter(opt.value); setOffset(0); }}
            className={clsx(
              "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
              stateFilter === opt.value
                ? "bg-orange-100 text-orange-700 border border-orange-200"
                : "text-gray-500 hover:bg-gray-100"
            )}
          >
            <opt.icon className="h-3.5 w-3.5" />
            {opt.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-sm text-red-700">{(error as Error).message}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      )}

      {/* Listing Detail Modal */}
      {selectedListing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedListing(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto m-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 pr-4">{selectedListing.title}</h2>
              <button onClick={() => setSelectedListing(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>

            {/* Images */}
            {selectedListing.images && selectedListing.images.length > 0 && (
              <div className="mb-4 flex gap-2 overflow-x-auto pb-2">
                {selectedListing.images.map((img) => (
                  <img
                    key={img.listing_image_id}
                    src={img.url_570xN}
                    alt=""
                    className="h-32 w-32 rounded-lg object-cover flex-shrink-0"
                  />
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div>
                <span className="text-gray-500">Fiyat:</span>
                <span className="ml-2 font-medium">{formatPrice(selectedListing.price)}</span>
              </div>
              <div>
                <span className="text-gray-500">Stok:</span>
                <span className="ml-2 font-medium">{selectedListing.quantity}</span>
              </div>
              <div>
                <span className="text-gray-500">Görüntülenme:</span>
                <span className="ml-2 font-medium">{selectedListing.views}</span>
              </div>
              <div>
                <span className="text-gray-500">Favori:</span>
                <span className="ml-2 font-medium">{selectedListing.num_favorers}</span>
              </div>
              <div>
                <span className="text-gray-500">Oluşturulma:</span>
                <span className="ml-2 font-medium">{formatDate(selectedListing.creation_tsz)}</span>
              </div>
              <div>
                <span className="text-gray-500">Son Güncelleme:</span>
                <span className="ml-2 font-medium">{formatDate(selectedListing.last_modified_tsz)}</span>
              </div>
            </div>

            {selectedListing.tags.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-1">Etiketler:</p>
                <div className="flex flex-wrap gap-1">
                  {selectedListing.tags.map((tag) => (
                    <span key={tag} className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-1">Açıklama:</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                {selectedListing.description}
              </p>
            </div>

            <div className="flex gap-2">
              <a
                href={selectedListing.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600"
              >
                <ExternalLink className="h-4 w-4" />
                Etsy'de Gör
              </a>
              <button
                onClick={() => setSelectedListing(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Listings Grid */}
      {!isLoading && listings.length === 0 && (
        <div className="text-center py-16">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Bu kategoride ürün bulunamadı</p>
        </div>
      )}

      {!isLoading && listings.length > 0 && (
        <div className="grid gap-3">
          {listings.map((listing) => {
            const thumbnail = listing.images?.[0]?.url_170x135;
            return (
              <div
                key={listing.listing_id}
                className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow"
              >
                {/* Thumbnail */}
                <div className="h-16 w-16 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden">
                  {thumbnail ? (
                    <img src={thumbnail} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Package className="h-6 w-6 text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 truncate">{listing.title}</h3>
                  <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      {formatPrice(listing.price)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Archive className="h-3 w-3" />
                      Stok: {listing.quantity}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {listing.views}
                    </span>
                    {listing.tags.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        {listing.tags.length} etiket
                      </span>
                    )}
                  </div>
                </div>

                {/* State Badge */}
                <div className="flex-shrink-0">
                  {(() => {
                    const stateOpt = STATE_OPTIONS.find((s) => s.value === listing.state);
                    if (!stateOpt) return null;
                    return (
                      <span className={clsx("rounded-full px-2.5 py-1 text-xs font-medium", stateOpt.color)}>
                        {stateOpt.label}
                      </span>
                    );
                  })()}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => navigate(`/listing-builder?etsyId=${listing.listing_id}`)}
                    className="flex items-center gap-1 rounded-lg px-2.5 py-2 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 text-xs font-medium"
                    title="Düzenle ve Etsy'ye güncelle"
                  >
                    <Edit3 className="h-4 w-4" /> Düzenle
                  </button>
                  <button
                    onClick={() => setSelectedListing(listing)}
                    className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Detay"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <a
                    href={listing.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-orange-600"
                    title="Etsy'de Gör"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <button
                    onClick={() => {
                      if (confirm("Bu ürünü silmek istediğinize emin misiniz?")) {
                        deleteM.mutate(listing.listing_id);
                      }
                    }}
                    disabled={deleteM.isPending}
                    className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    title="Sil"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Sayfa {currentPage} / {totalPages} ({totalCount} ürün)
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
              Önceki
            </button>
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= totalCount}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              Sonraki
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
